import { Clock, Mic, MicOff, SkipForward } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import demoAvatar from '../../../assets/images/demoAvatar.png';
import { useAppDispatch } from '../../../redux/store/hooks';
import { useHrGenerateTestMutation } from '../../../services/hrApi';
// import { APIError } from '../../HrRound/Instructions';

type InterviewPreviewProps = {
  onStartInterview?: () => void;
  isHr?: boolean;
};

const Step3SetUP = ({ onStartInterview, isHr }: InterviewPreviewProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const [previewTimer, setPreviewTimer] = useState(15);
  const dispatch = useAppDispatch();
  const [hrGenerateTest, { isLoading }] = useHrGenerateTestMutation();

  const handleClickStartInterview = () => {
    onStartInterview ? onStartInterview() : null;
  };

  // Initialize camera
  useEffect(() => {
    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setCameraEnabled(true);
      } catch (error) {
        console.error('Error accessing camera:', error);
        setCameraEnabled(false);
      }
    };

    initCamera();

    // Cleanup function
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Preview timer countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setPreviewTimer(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const minutes = Math.floor(previewTimer / 60);
  const remainingSeconds = previewTimer % 60;

  return (
    <div className='flex items-center justify-center bg-opacity-60 md:p-4'>
      <div className='w-full max-w-6xl'>
        <div className='overflow-hidden rounded-3xl bg-white shadow-2xl'>
          <div className='bg-slate-900 text-white'>
            <div className='hidden items-center justify-between px-6 py-4 md:flex'>
              <div className='flex items-center gap-3'>
                <div className='flex items-center gap-3 text-sm'>
                  <span className='flex items-center gap-2 rounded-2xl bg-red-500/40 px-3 py-1 text-sm font-medium text-red-600'>
                    <span className='h-2 w-2 animate-pulse rounded-full bg-red-600' />
                    REC
                  </span>
                </div>
                <span className='text-sm font-medium'>
                  {isHr ? 'HR Interview' : 'Google Interview'}
                </span>
              </div>

              <div className='flex items-center gap-2 font-semibold text-green-600'>
                <Clock className='h-4 w-4' />
                {minutes}:{remainingSeconds.toString().padStart(2, '0')}
              </div>

              <button
                disabled
                className='flex max-w-40 cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold opacity-75'
              >
                Next Question
                <SkipForward className='h-4 w-4' />
              </button>
            </div>

            <div className='flex flex-col md:hidden'>
              <div className='flex items-center justify-between px-4 py-3'>
                <div className='flex w-full items-center justify-between gap-3'>
                  <span className='flex items-center gap-2 rounded-2xl bg-red-500/40 px-3 py-1 text-sm font-medium text-red-600'>
                    <span className='h-2 w-2 animate-pulse rounded-full bg-red-600' />
                    REC
                  </span>
                  <span className='text-sm font-medium'>
                    {isHr ? 'HR Interview' : 'Google Interview'}
                  </span>
                </div>
              </div>

              <div className='flex items-center justify-between gap-3 px-4 pb-3'>
                <div className='flex items-center gap-2 font-semibold text-green-600'>
                  <Clock className='h-4 w-4' />
                  0:00
                </div>

                <button
                  disabled
                  className='flex cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold opacity-75'
                >
                  Next Question
                  <SkipForward className='h-4 w-4' />
                </button>
              </div>
            </div>
          </div>

          <div className='grid grid-cols-1 gap-6 p-6 md:grid-cols-3'>
            <div className='md:col-span-2'>
              <div className='relative rounded-2xl bg-gradient-to-br from-rose-100 to-rose-200 shadow-lg'>
                <div className='flex flex-col items-center'>
                  <img
                    src={demoAvatar}
                    alt='Interviewer Avatar'
                    className='mx-auto h-96 w-full rounded-2xl shadow-md'
                  />
                </div>
              </div>
              <div className='my-5'>
                <p className='font-bold'>Question 1 of {isHr ? 6 : 10}</p>
                <p className='my-3 text-gray-700'>
                  Tell me about a challenging project you worked on and how you
                  overcame the obstacles.
                </p>
              </div>
            </div>

            <div className='flex flex-col items-center justify-between rounded-2xl bg-slate-900 p-6 text-white shadow-lg'>
              <div className='relative h-56 w-full overflow-hidden rounded-xl bg-slate-800'>
                {cameraEnabled ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className='h-full w-full object-cover'
                  />
                ) : (
                  <div className='flex h-full w-full items-center justify-center'>
                    <span className='text-sm text-gray-400'>Camera Off</span>
                  </div>
                )}
              </div>

              <div className='mt-6'>
                <div className='flex flex-col items-center gap-3'>
                  <div className='flex h-24 w-24 items-center justify-center rounded-full border-4 border-white bg-slate-800 text-3xl font-bold text-white'>
                    2:00
                  </div>
                  <p className='text-sm text-gray-400'>Answer Time Remaining</p>
                </div>
              </div>
            </div>
          </div>

          <div className='border-t border-gray-200 bg-gray-50 px-6 py-4'>
            <div className='mb-4 grid grid-cols-1 gap-4 md:grid-cols-3'>
              <div className='flex items-start gap-3'>
                <div className='rounded-full bg-blue-100 p-2'>
                  <Clock className='h-5 w-5 text-blue-600' />
                </div>
                <div>
                  <h4 className='font-semibold text-gray-900'>Time Limit</h4>
                  <p className='text-sm text-gray-600'>
                    2 minutes per question
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='rounded-full bg-green-100 p-2'>
                  <Mic className='h-5 w-5 text-green-600' />
                </div>
                <div>
                  <h4 className='font-semibold text-gray-900'>Recording</h4>
                  <p className='text-sm text-gray-600'>
                    Audio & video will be captured
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='rounded-full bg-purple-100 p-2'>
                  <MicOff className='h-5 w-5 text-purple-600' />
                </div>
                <div>
                  <h4 className='font-semibold text-gray-900'>One Take</h4>
                  <p className='text-sm text-gray-600'>
                    You cannot re-record answers
                  </p>
                </div>
              </div>
            </div>

            <div className='flex flex-col items-center justify-between gap-3 md:flex-row'>
              <p className='text-sm text-gray-600'>
                This is how the interview interface will look. Make sure your
                camera and microphone are working properly.
              </p>
              <button
                onClick={handleClickStartInterview}
                className='rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-blue-700'
              >
                Start Interview
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step3SetUP;
